import type { ReactElement } from 'react';
import { Link } from '@tanstack/react-router';

/**
 * Página índice de ajustes.
 *
 * En M1 solo lista Strava. En M2 se añadirán Google Calendar, exportaciones,
 * backup/restore, etc. Mantengo la estructura de "lista de tarjetas con link"
 * para que añadir nuevas integraciones sea trivial.
 */
export function SettingsPage(): ReactElement {
  return (
    <section aria-labelledby="settings-title" className="space-y-6">
      <header>
        <h1
          id="settings-title"
          className="text-2xl font-semibold tracking-tight text-slate-900"
        >
          Ajustes
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          Conecta servicios externos y configura tu cuenta.
        </p>
      </header>

      <ul aria-label="Integraciones disponibles" className="space-y-3">
        <li>
          <Link
            to="/ajustes/strava"
            className="block rounded-lg border border-slate-200 bg-white p-4 shadow-sm hover:border-teal-600 hover:shadow focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
          >
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-base font-semibold text-slate-900">Strava</h2>
                <p className="mt-1 text-sm text-slate-600">
                  Sincroniza tus actividades (ciclismo, carrera, natación) desde
                  Strava para que el coach las analice.
                </p>
              </div>
              <span aria-hidden="true" className="text-slate-400">
                →
              </span>
            </div>
          </Link>
        </li>
        <li>
          <Link
            to="/ajustes/anthropic"
            className="block rounded-lg border border-slate-200 bg-white p-4 shadow-sm hover:border-teal-600 hover:shadow focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
          >
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-base font-semibold text-slate-900">Anthropic</h2>
                <p className="mt-1 text-sm text-slate-600">
                  Configura tu API key de Claude para que el coach pueda
                  generar planes.
                </p>
              </div>
              <span aria-hidden="true" className="text-slate-400">
                →
              </span>
            </div>
          </Link>
        </li>
        <li>
          <Link
            to="/ajustes/google"
            className="block rounded-lg border border-slate-200 bg-white p-4 shadow-sm hover:border-teal-600 hover:shadow focus:outline-none focus:ring-2 focus:ring-teal-600 focus:ring-offset-2"
          >
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-base font-semibold text-slate-900">Google Calendar</h2>
                <p className="mt-1 text-sm text-slate-600">
                  Envía tus entrenamientos planificados a Google Calendar o
                  descárgalos en formato .ics.
                </p>
              </div>
              <span aria-hidden="true" className="text-slate-400">
                →
              </span>
            </div>
          </Link>
        </li>
        {/*
          M2.6: la card de Voyage / Biblioteca (RAG sobre PDFs) se retira
          de Ajustes. El módulo M2.3 sigue presente en el repo y la ruta
          /biblioteca sigue funcionando vía URL directa para no perder el
          código; cuando se reactive (flag RAG_ENABLED=true en backend)
          basta con restaurar este `<li>` aquí. No se ha eliminado el
          cliente `lib/voyage.ts` ni `lib/knowledge.ts`.

          No introducimos una sección "Avanzado / Integraciones inactivas"
          ahora: con una sola card oculta no compensa añadir un acordeón
          que el usuario probablemente nunca abra.
        */}
      </ul>
    </section>
  );
}
